import { useState, useEffect } from "react";
import Topbar from "../components/layout/Topbar.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { updateProfile, changePassword as changePasswordService, toggleMfa } from "../services/userService.js";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const isAdmin = true;

  const [activeTab, setActiveTab] = useState("profile");

  // ── Informations profil (contrôlé) ──────────────────────────
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName:  "",
    email:     "",
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved,  setProfileSaved]  = useState(false);
  const [profileError,  setProfileError]  = useState("");

  // ── Changement de mot de passe (contrôlé) ───────────────────
  const [pwdForm, setPwdForm] = useState({
    oldPassword:     "",
    newPassword:     "",
    confirmPassword: "",
  });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdSaved,  setPwdSaved]  = useState(false);
  const [pwdError,  setPwdError]  = useState("");

  // ── Notifications ────────────────────────────────────────────
  const [notifPrefs, setNotifPrefs] = useState({ email: true, inapp: true, digest: "immediate" });
  const [notifSaved, setNotifSaved] = useState(false);

  // ── Activation / désactivation du 2FA à la connexion ────────
  const [mfaEnabled, setMfaEnabled] = useState(true);
  const [mfaSaving,  setMfaSaving]  = useState(false);
  const [mfaError,   setMfaError]   = useState("");

  // ── Initialise les formulaires depuis le user du contexte ───
  useEffect(() => {
    if (user) {
      setProfileForm({
        firstName: user.firstName ?? "",
        lastName:  user.lastName  ?? "",
        email:     user.email     ?? "",
      });
      setMfaEnabled(user.mfaEnabled ?? true);
    }
  }, [user]);

  // ─────────────────────────────────────────────────────────────
  //  Sauvegarde du profil  →  PUT /api/users/profile
  // ─────────────────────────────────────────────────────────────
  const saveProfile = async () => {
    setProfileError("");
    setProfileSaving(true);
    try {
      const res = await updateProfile({
        firstName: profileForm.firstName,
        lastName:  profileForm.lastName,
      });
      if (setUser && res?.data) setUser(res.data);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2000);
    } catch (err) {
      setProfileError(err.message || "Erreur lors de la mise à jour du profil.");
    } finally {
      setProfileSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  //  Changement de mot de passe  →  PUT /api/users/change-password
  // ─────────────────────────────────────────────────────────────
  const changePassword = async () => {
    setPwdError("");

    if (!pwdForm.oldPassword || !pwdForm.newPassword || !pwdForm.confirmPassword) {
      setPwdError("Tous les champs sont obligatoires.");
      return;
    }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) {
      setPwdError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (pwdForm.newPassword.length < 8) {
      setPwdError("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    setPwdSaving(true);
    try {
      await changePasswordService(pwdForm.oldPassword, pwdForm.newPassword);
      setPwdSaved(true);
      setPwdForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
      setTimeout(() => setPwdSaved(false), 2000);
    } catch (err) {
      setPwdError(err.message || "Ancien mot de passe incorrect.");
    } finally {
      setPwdSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  //  Activation / désactivation du 2FA (email) à la connexion
  //  → PUT /api/users/mfa-toggle  { enabled: boolean }
  //
  //  Activé  : la connexion exige le code de vérification par email
  //            (comportement actuellement forcé pour tout le monde).
  //  Désactivé : connexion directe après email + mot de passe.
  // ─────────────────────────────────────────────────────────────
  const handleToggleMfa = async () => {
    const newValue = !mfaEnabled;
    setMfaError("");
    setMfaSaving(true);
    try {
      await toggleMfa(newValue);
      setMfaEnabled(newValue);
      if (setUser) setUser(prev => prev ? { ...prev, mfaEnabled: newValue } : prev);
    } catch (err) {
      setMfaError(err.message || "Impossible de modifier ce paramètre.");
    } finally {
      setMfaSaving(false);
    }
  };

  const saveNotifPrefs = () => {
    // TODO: brancher sur un endpoint dédié quand disponible côté backend
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 2000);
  };

  return (
    <>
      <Topbar title="Mon" subtitle="profil" admin={isAdmin} />
      <div className="page-body" style={{ maxWidth: 720 }}>

        {/* Avatar header */}
        <div className="itm-card anim-fade-up" style={{ marginBottom: 20, padding: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{ width: 72, height: 72, borderRadius: 18, background: "linear-gradient(135deg,var(--accent3),var(--accent))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, fontWeight: 800, color: "#fff", fontFamily: "Syne,sans-serif", flexShrink: 0 }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            <div>
              <div style={{ fontFamily: "Syne,sans-serif", fontSize: 20, fontWeight: 800, color: "var(--text-1)", marginBottom: 4 }}>
                {user?.firstName} {user?.lastName}
              </div>
              <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 6 }}>{user?.email}</div>
              <span className={`badge-status ${isAdmin ? "bs-active" : "bs-open"}`}>{isAdmin ? "Administrateur" : "Utilisateur client"}</span>
            </div>
          </div>
        </div>

        {/* Onglets */}
        <div className="itm-tabs">
          {[{ id: "profile", label: "Informations" }, { id: "security", label: "Sécurité" }, { id: "notifications", label: "Notifications" }].map(t => (
            <div key={t.id} className={`itm-tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>{t.label}</div>
          ))}
        </div>

        {/* Tab Profil */}
        {activeTab === "profile" && (
          <div className="itm-card anim-fade-in">

            {profileError && (
              <div style={{ background: "rgba(220,38,38,.08)", border: "1px solid rgba(220,38,38,.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#DC2626" }}>
                <i className="bi bi-exclamation-triangle-fill me-2" />{profileError}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="itm-label">Prénom</label>
                <input
                  className="itm-input"
                  value={profileForm.firstName}
                  onChange={e => setProfileForm(p => ({ ...p, firstName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="itm-label">Nom</label>
                <input
                  className="itm-input"
                  value={profileForm.lastName}
                  onChange={e => setProfileForm(p => ({ ...p, lastName: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="itm-label">Email</label>
                {/* Email en lecture seule — modification via un flux dédié si besoin */}
                <input className="itm-input" type="email" value={profileForm.email} readOnly style={{ opacity: .6, cursor: "not-allowed" }} />
              </div>
              <div className="form-group">
                <label className="itm-label">Téléphone</label>
                <input className="itm-input" placeholder="+33 6 00 00 00 00" />
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
              <button className="btn-itm btn-itm-primary" onClick={saveProfile} disabled={profileSaving}>
                {profileSaving
                  ? <><i className="bi bi-arrow-repeat spin" /> Enregistrement…</>
                  : profileSaved
                    ? <><i className="bi bi-check2" /> Enregistré !</>
                    : <><i className="bi bi-floppy" /> Enregistrer</>
                }
              </button>
            </div>
          </div>
        )}

        {/* Tab Sécurité — mot de passe */}
        {activeTab === "security" && (
          <div className="itm-card anim-fade-in">
            <div className="card-title" style={{ marginBottom: 16 }}>Changer le mot de passe</div>

            {pwdError && (
              <div style={{ background: "rgba(220,38,38,.08)", border: "1px solid rgba(220,38,38,.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#DC2626" }}>
                <i className="bi bi-exclamation-triangle-fill me-2" />{pwdError}
              </div>
            )}

            <div className="form-group">
              <label className="itm-label">Mot de passe actuel</label>
              <input
                className="itm-input" type="password" placeholder="••••••••"
                value={pwdForm.oldPassword}
                onChange={e => setPwdForm(p => ({ ...p, oldPassword: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="itm-label">Nouveau mot de passe</label>
              <input
                className="itm-input" type="password" placeholder="Minimum 8 caractères"
                value={pwdForm.newPassword}
                onChange={e => setPwdForm(p => ({ ...p, newPassword: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="itm-label">Confirmer le nouveau mot de passe</label>
              <input
                className="itm-input" type="password" placeholder="••••••••"
                value={pwdForm.confirmPassword}
                onChange={e => setPwdForm(p => ({ ...p, confirmPassword: e.target.value }))}
              />
            </div>
            <div style={{ padding: "10px 14px", background: "var(--accent-dim)", borderRadius: 10, fontSize: 12, color: "var(--accent)", marginBottom: 16 }}>
              <i className="bi bi-shield-check me-2" />
              Règles : min. 8 caractères, 1 majuscule, 1 chiffre, 1 caractère spécial.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn-itm btn-itm-primary" onClick={changePassword} disabled={pwdSaving}>
                {pwdSaving
                  ? <><i className="bi bi-arrow-repeat spin" /> Modification…</>
                  : pwdSaved
                    ? <><i className="bi bi-check2" /> Modifié !</>
                    : <><i className="bi bi-lock" /> Changer le mot de passe</>
                }
              </button>
            </div>
          </div>
        )}

        {/* Tab Sécurité — double authentification à la connexion */}
        {activeTab === "security" && (
          <div className="itm-card anim-fade-in" style={{ marginTop: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--accent-dim)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="bi bi-shield-lock-fill" style={{ color: "var(--accent)", fontSize: 16 }} />
              </div>
              <div style={{ flex: 1 }}>
                <div className="card-title" style={{ marginBottom: 0 }}>Double authentification</div>
                <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>
                  Un code de vérification vous sera envoyé par email à chaque connexion.
                </div>
              </div>
            </div>

            {mfaError && (
              <div style={{ background: "rgba(220,38,38,.08)", border: "1px solid rgba(220,38,38,.2)", borderRadius: 10, padding: "10px 14px", margin: "12px 0", fontSize: 13, color: "#DC2626" }}>
                <i className="bi bi-exclamation-triangle-fill me-2" />{mfaError}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0 4px 0" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-1)" }}>
                  {mfaEnabled ? "Activée" : "Désactivée"}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                  {mfaEnabled
                    ? "Votre compte est protégé par un code envoyé par email à chaque connexion."
                    : "Connexion directe après email + mot de passe, sans code de vérification."}
                </div>
              </div>

              {/* Toggle switch */}
              <div
                onClick={() => !mfaSaving && handleToggleMfa()}
                style={{
                  width: 44, height: 24, borderRadius: 12,
                  cursor: mfaSaving ? "not-allowed" : "pointer",
                  opacity: mfaSaving ? 0.6 : 1,
                  background: mfaEnabled ? "var(--accent)" : "var(--navy-soft)",
                  border: "1px solid var(--border)", position: "relative", transition: "background .2s",
                  flexShrink: 0,
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: "50%", background: "#fff",
                  position: "absolute", top: 2, transition: "left .2s",
                  left: mfaEnabled ? 22 : 2,
                }} />
              </div>
            </div>

            {!mfaEnabled && (
              <div style={{
                marginTop: 12, padding: "10px 14px", background: "rgba(220,38,38,.06)",
                border: "1px solid rgba(220,38,38,.2)", borderRadius: 10,
                fontSize: 12, color: "#DC2626", display: "flex", alignItems: "center", gap: 8,
              }}>
                <i className="bi bi-exclamation-triangle-fill" />
                La double authentification est désactivée pour ce compte administrateur.
              </div>
            )}
          </div>
        )}

        {/* Tab Notifications */}
        {activeTab === "notifications" && (
          <div className="itm-card anim-fade-in">
            <div className="card-title" style={{ marginBottom: 16 }}>Préférences de notification</div>
            {[
              { key: "email", label: "Notifications par email", desc: "Recevoir des emails lors des changements de statut" },
              { key: "inapp", label: "Notifications in-app",    desc: "Afficher les notifications dans l'interface" },
            ].map(p => (
              <div key={p.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid var(--border)" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-1)" }}>{p.label}</div>
                  <div style={{ fontSize: 12, color: "var(--text-3)" }}>{p.desc}</div>
                </div>
                <div style={{
                  width: 44, height: 24, borderRadius: 12, cursor: "pointer", transition: "background .2s",
                  background: notifPrefs[p.key] ? "var(--accent)" : "var(--navy-soft)",
                  border: "1px solid var(--border)", position: "relative"
                }} onClick={() => setNotifPrefs(prev => ({ ...prev, [p.key]: !prev[p.key] }))}>
                  <div style={{
                    width: 18, height: 18, borderRadius: "50%", background: "#fff",
                    position: "absolute", top: 2, transition: "left .2s",
                    left: notifPrefs[p.key] ? 22 : 2
                  }} />
                </div>
              </div>
            ))}
            <div style={{ marginTop: 16 }}>
              <label className="itm-label">Fréquence des emails</label>
              <select className="itm-select" style={{ width: "100%" }} value={notifPrefs.digest} onChange={e => setNotifPrefs(p => ({ ...p, digest: e.target.value }))}>
                <option value="immediate">Immédiat (à chaque événement)</option>
                <option value="daily">Résumé quotidien</option>
                <option value="weekly">Résumé hebdomadaire</option>
              </select>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button className="btn-itm btn-itm-primary" onClick={saveNotifPrefs}>
                {notifSaved ? <><i className="bi bi-check2" /> Enregistré !</> : <><i className="bi bi-floppy" /> Enregistrer</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}